import Invoice from '../../common/models/Invoice.ts';
import Driver from '../types/Driver.ts';
import PdfPageWithText from '../types/PdfPageWithText.ts';
import PdfProcessedPage from '../types/PdfProcessedPage.ts';
import PdfTextTest, { StringAtIndexTest } from '../types/PdfTextTest.ts';
import { compute_pdf_line_items, compute_pdf_tests } from '../pdf_parser.ts';
import { stamp } from '../pdf_writer.ts';
import PdfLineItem from '../types/PdfLineItem.ts';
import _, { sortBy } from 'lodash'


export default class extends Driver {
    constructor() {
        super()
    }

    parse_project_NaN(pdf_text_contents: string[]): string {
        const tests = [
            {
                "string": "PO",
                "elements_to_skip": 2
            } as StringAtIndexTest
        ]

        let pdf_tests = {
            "first_string": 'CUST',
            "tests": tests,
            "elements_to_skip_to_return": 4
        } as PdfTextTest

        return compute_pdf_tests(pdf_text_contents, pdf_tests) ?? ""
    }
    
    parse_invoice_NaN(pdf_text_contents: string[]): string {
        const tests = [
            {
                "string": "NO.",
                "elements_to_skip": 2
            } as StringAtIndexTest
        ]

        let pdf_tests = {
            "first_string": 'INVOICE',
            "tests": tests,
            "elements_to_skip_to_return": 4
        } as PdfTextTest
    
        return compute_pdf_tests(pdf_text_contents, pdf_tests) ?? ""
    }
    
    parse_total(pdf_text_contents: string[]): number | null {
        const tests = [
            {
                "string": "TOTAL",
                "elements_to_skip": 2
            } as StringAtIndexTest
        ]
        
        let pdf_tests = {
            "first_string": 'INVOICE',
            "tests": tests,
            "elements_to_skip_to_return": 11
        } as PdfTextTest

        const total = compute_pdf_tests(pdf_text_contents, pdf_tests)
    
        return this.parse_money_string(total) ?? 0
    }

    parse_invoice_line_items(pdf_text_contents: any): PdfLineItem[] | null {
        let quantities_with_postion: object[] = _.chain(pdf_text_contents.items)
            .filter((content) => {
                return content.transform[4] > 22 
                    && content.transform[4] < 88
                    && content.transform[5] > 350
                    && content.transform[5] < 466
            })
            .groupBy((content) => {
                return content.transform[5]
            })
            .map((contentGroup) => {
                return {
                    "y": contentGroup[0].transform[5],
                    "text": contentGroup
                        .map((contentGroupItem) => contentGroupItem.str)
                        .join("")
                }
            })
            .sortBy("y")
            .reverse()
            .value()
        
        const row_Ys = quantities_with_postion.map((quantity_with_postion: object) => quantity_with_postion.y)

        const quantities: number[] = quantities_with_postion.map((quantity_with_postion) => parseInt(quantity_with_postion.text.split(" ")[0]))

        const ship_quantities: number[] = _.chain(pdf_text_contents.items)
            .filter((content) => {
                return content.transform[4] > 88 
                    && content.transform[4] < 154
                    && content.transform[5] > 350
                    && content.transform[5] < 466
            })
            .groupBy((content) => {
                return content.transform[5]
            })
            .map((contentGroup) => {
                return parseInt(
                    contentGroup
                        .map((contentGroupItem) => contentGroupItem.str)
                        .join("")
                        .split(" ")[0]
                )
            })
            .reverse()
            .value()

        let part_names: string[] = _.chain(pdf_text_contents.items)
            .filter((content) => {
                return content.transform[4] > 154 
                    && content.transform[4] < 352
                    && content.transform[5] > 350
                    && content.transform[5] < 466
            })
            .groupBy((content) => {
                return content.transform[5]
            })
            .map((contentGroup) => {
                return {
                    "y": contentGroup[0].transform[5],
                    "text": contentGroup
                        .map((contentGroupItem) => contentGroupItem.str)
                        .join("")
                }
            })
            .sortBy("y")
            .reverse()
            .groupBy((text_position) => {
                for (let i = 0; i < row_Ys.length; i++) {
                    if ((i + 1) >= row_Ys.length) {
                        return row_Ys[row_Ys.length - 1]
                    } else if (text_position.y <= Math.ceil(row_Ys[i]) && text_position.y > row_Ys[i + 1]) {
                        return row_Ys[i]
                    }
                }

                return row_Ys[0]
            })
            .map((text_positions) => {
                let texts = text_positions
                    .map((text_position) => text_position.text)

                texts.shift()

                return texts.join(" ")
            })
            .reverse()
            .value()

        
        let unit_prices_and_units_of_measure: string[] = _.chain(pdf_text_contents.items)
            .filter((content) => {
                return content.transform[4] > 417
                    && content.transform[4] < 507
                    && content.transform[5] > 350
                    && content.transform[5] < 466
            })
            .groupBy((content) => {
                return content.transform[5]
            })
            .map((contentGroup) => {
                return contentGroup
                    .map((contentGroupItem) => contentGroupItem.str)
                    .join("")
            })
            .reverse()
            .value()

        const line_prices: number[] = _.chain(pdf_text_contents.items)
            .filter((content) => {
                return content.transform[4] > 507
                    && content.transform[4] < 597
                    && content.transform[5] > 350
                    && content.transform[5] < 466
            })
            .groupBy((content) => {
                return content.transform[5]
            })
            .map((contentGroup) => {
                return parseFloat(
                    contentGroup
                        .map((contentGroupItem) => contentGroupItem.str)
                        .join("")
                        .replace("$", "")
                        .replace(",", "")
                )
            })
            .reverse()
            .value()

        const units_of_measure: string[] = unit_prices_and_units_of_measure.map((unit_price_and_unit_of_measure) => unit_price_and_unit_of_measure.split("/")[1])
        const unit_prices: number[] = unit_prices_and_units_of_measure.map((unit_price_and_unit_of_measure) => parseFloat(unit_price_and_unit_of_measure.split("/")[0].replace("$", "").replace(",", "")))

        return row_Ys.map((row, index) => {
            return {
                line_number: index + 1,
                part_name: part_names[index],
                part_description: "",
                quantity: quantities[index],
                quantity_shipped: ship_quantities[index],
                quantity_back_ordered: quantities[index] - ship_quantities[index],
                unit_of_measure: units_of_measure[index],
                price_per_unit: unit_prices[index],
                line_item_total: line_prices[index]
            } as PdfLineItem
        })
    }

    check(pdf_text_contents: string[]): boolean {
        const tests = [
            {
                "string": "BOX",
                "elements_to_skip": 2
            } as StringAtIndexTest,
            {
                "string": "500",
                "elements_to_skip": 4
            } as StringAtIndexTest,
            {
                "string": "EAST",
                "elements_to_skip": 5
            } as StringAtIndexTest,
            {
                "string": "DUBUQUE,",
                "elements_to_skip": 7
            } as StringAtIndexTest,
            {
                "string": "IL",
                "elements_to_skip": 9
            } as StringAtIndexTest,
        ]

        let pdf_tests = {
            "first_string": 'PO',
            "tests": tests,
            "elements_to_skip_to_return": 11
        } as PdfTextTest

        return compute_pdf_tests(pdf_text_contents, pdf_tests) === "61025-4418"
    }

    parse_pages(pages: PdfPageWithText[]): PdfProcessedPage[] {
        

        return pages.map((page: PdfPageWithText) => {
            return new PdfProcessedPage({
                page_number: page.page_number,
                vendor: 'Crescent',
                line_items: this.parse_invoice_line_items(page.text_contents_full),
                project_NaN: this.parse_project_NaN(page.text_contents),
                invoice_NaN: this.parse_invoice_NaN(page.text_contents),
                total: this.parse_total(page.text_contents)
            })
        })
    }

    async stamp_invoices(invoices: Invoice[]): Promise<Invoice[]> {
        const x_coordinate = 185
        const y_coordinate = 800

        for (const index in invoices) {
            const invoice = invoices[index]
            if (invoice.pdf_bytes === null) {
                continue
            }

            const stamped_pdf_bytes = await stamp(x_coordinate, y_coordinate, invoice.invoice_information, invoice.pdf_bytes)
            invoices[index].stamped_pdf_bytes = stamped_pdf_bytes
            invoices[index].stamp_x = x_coordinate
            invoices[index].stamp_y = y_coordinate
        }

        return invoices  
    }
}
